import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core";
import { v4 as uuidv4 } from "uuid";
import { useNotificationStore } from "./notification";
import { useLoaderStore } from "./loader";
import { useMyOrdersStore } from "./myOrders";
import { useFileUploadStore } from "./address";
import { usePdfStore } from "./pdf";
import { useContactStore } from "./contactInfo";
import useDeliveryStore from "./delivery";
import { usePriceStore } from "./price";
import config from "@/config";
import {api} from '@/services/http';
import {API_URLS} from "@/services/api-config";
import CreateMailoutApi from "@/api";
import type { OAuthToken } from "@/types/user";
import { User } from "@/types/user";
import {
  tokenExists,
  tokenHasExpired,
  generateCodeChallenge,
  generateCodeVerifier,
} from '@/utils/common';
import constant from '@/constant';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    redirectionPath: useStorage<string>('redirectionPath', '/'),
    codeVerifier: useStorage<string>('codeVerifier', ''),
    autoLogoutTime: useStorage<Date>('autoLogoutTime', new Date('2020-01-01')),
    pnAccessToken: useStorage<string>('pnAccessToken', ''),
    deviceId: useStorage<string>('deviceId', uuidv4()),
    isLoggedIn: false,
    user: null as User | null,
  }),
  actions: {
    setRedirectionPath(data: string) {
      this.redirectionPath = data;
    },
    setCodeVerifier(data: string) {
      this.codeVerifier = data;
    },
    setAutoLogoutTime(data: Date) {
      this.autoLogoutTime = data;
    },
    setAccessToken(data: string) {
      this.pnAccessToken = data;
    },
    setIsLoggedIn(data: boolean) {
      this.isLoggedIn = data;
    },
    setUser(data: User) {
      this.user = data;
    },
    async createPnToken(code: string, redirectUri: string, verifier: string): Promise<OAuthToken> {
      const body = new URLSearchParams();
      const { AUTH_TOKEN } = constant;
      body.append('grant_type', AUTH_TOKEN.GRANT_TYPE);
      body.append('client_id', config.VUE_APP_AUTH_ID);
      body.append('code', code);
      body.append('redirect_uri', redirectUri);
      body.append('code_verifier', verifier);
      const result = await api.post<OAuthToken>(`${API_URLS.authToken}`, body, {
        headers: {
          'Content-Type': AUTH_TOKEN.CONTENT_TYPE,
        },
      });
      return result;
    },
    async authenticateUser(code: string): Promise<string> {
      const loaderStore = useLoaderStore();
      loaderStore.showLoader();
      try {
        const redirectUri = `${window.location.origin}/auth`;
        const data = await this.createPnToken(code, redirectUri, this.codeVerifier);
        this.setCodeVerifier('');
        const autoLogoutInMilliseconds = (data.expires_in - 60) * 1000; // 1 minute before token expires
        const nowMilliseconds = new Date().getTime();
        const autoLogoutTime = new Date(nowMilliseconds + autoLogoutInMilliseconds);
        localStorage.setItem('autoLogoutTime', autoLogoutTime.toISOString());
        localStorage.setItem('pnAccessToken', data.access_token);
        this.setAutoLogoutTime(autoLogoutTime);
        this.setIsLoggedIn(true);
        const path = this.redirectionPath;
        this.setRedirectionPath('');
        const user = await this.getCurrentUserInfo();
        this.setUser(user);
        return path;
      } catch (error) {
        const notificationStore = useNotificationStore();
        notificationStore.addNotification({
          messageKey: 'loginErrorMessage',
          closable: true,
          type: 'error',
          hide: false,
        });
        //hide loader for error
        loaderStore.hideLoader();
        console.error(error);
        return '/';
      }
    },
    async getCurrentUserInfo(): Promise<User> {
      const loaderStore = useLoaderStore();
      //show loader if not already shown, to avoid showing loader twice in authenticateUser flow
      if (!loaderStore.isLoading) {
        loaderStore.showLoader();
      }
      try {
        const response = await CreateMailoutApi.getCurrentUserInfo();
        return response;
      } catch (e: any) {
        const notificationStore = useNotificationStore();
        notificationStore.addNotification({
          messageKey: 'loginErrorMessage',
          closable: true,
          type: 'error',
          hide: false,
        });
        console.log('CreateMail:APi - error in retrieving currentUserInfo');
        throw e;
      } finally {
        loaderStore.hideLoader();
      }
    },

    redirectToLogout() {
      console.log('redirect to logout');
      const redirectURI = `${window.location.origin}/logout`;
      const state = {
        lang: localStorage['language'],
      };
      const stateEncoded = btoa(JSON.stringify(state));
      const queryParams = new URLSearchParams({
        post_logout_redirect_uri: redirectURI,
        state: stateEncoded,
      });
      const url = `${API_URLS.logoutUrl}?${queryParams.toString()}`;
      window.location.replace(url);
    },

    logout() {
      console.log('logout');
      localStorage.removeItem('orderDraft');
      localStorage.removeItem('selectedDate');
      localStorage.removeItem('priceData');
      localStorage.removeItem('pnIdToken');

      this.setIsLoggedIn(false);
      this.setAccessToken("");
      this.setRedirectionPath("");
      this.setCodeVerifier("");
      // Reset all order-related in-memory stores
      useMyOrdersStore().clearDraft();
      useFileUploadStore().clearFile();
      usePdfStore().clearPdfData();
      useContactStore().saveContact({
        companyName: '', organisationNumber: '',
        customerNumber: '',
        firstName: '', lastName: '',
        address: '', postalCode: '', city: '',
        email: '', mobile: '',
      });
      useDeliveryStore().setSelectedDate('');
      usePriceStore().resetPrice();
    },

    redirectToPnLogin(redirect: string) {
      console.log(`redirect to pn login with redir param ${redirect}`);
      this.setRedirectionPath(redirect);
      this.setCodeVerifier(generateCodeVerifier());
      const codeChallenge = generateCodeChallenge(this.codeVerifier);
      const redirectURI = `${window.location.origin}/auth`;
      const language = localStorage['language'];
      const stateEncoded = btoa(
        JSON.stringify({
          lang: language,
        })
      );

      const queryParams = new URLSearchParams({
        response_type: 'code',
        client_id: config.VUE_APP_AUTH_ID,
        redirect_uri: redirectURI,
        scope: 'openid',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: stateEncoded,
        ui_locales: language,
      });

      const loginURL = `${API_URLS.loginUrl}?${queryParams.toString()}`;
      window.location.replace(loginURL);
    },

    async init() {
      console.log('init auth store');
      if (tokenExists() && !tokenHasExpired()) {
        try {
          const user = await this.getCurrentUserInfo();
          this.setUser(user);
          this.setIsLoggedIn(true);
        } catch (e: any) {
          console.error('error while initialising auth store' + e.response.data);
          this.logout();
        }
      }
    },
  },
});
